import type { FC } from 'react';
import { Text } from '@launchpad-ui/components';

import { vars } from '../../../../css/vars';
import type { BaseContentProps } from '../Content';

export interface TextContentProps extends BaseContentProps {
  /**
   * The text content to display.
   * If not provided, displays "Empty".
   */
  text?: string;

  /**
   * Whether to disable text truncation (truncation is enabled by default)
   * @default false
   */
  disableTruncation?: boolean;

  /**
   * Optional tooltip text to show on hover.
   * If not provided and truncation is enabled, the full text will be shown as a tooltip.
   */
  tooltip?: string;

  /**
   * Text style variant
   * @default 'default'
   */
  variant?: 'strong' | 'default' | 'small';

  /**
   * Optional href to render as a link. When provided, renders as an <a> tag.
   */
  href?: string;
}

/**
 * Text content component for use inside Cell.
 *
 * @example
 * ```tsx
 * // Regular text
 * <Cell kind="regular" size="default">
 *   <TextContent text="Hello World" />
 * </Cell>
 *
 * // Text without truncation
 * <Cell kind="title" size="compact">
 *   <TextContent text="Very long text" disableTruncation />
 * </Cell>
 *
 * // Strong text
 * <Cell kind="regular" size="default">
 *   <TextContent text="Important" variant="strong" />
 * </Cell>
 *
 * // Small text
 * <Cell kind="regular" size="default">
 *   <TextContent text="Secondary info" variant="small" />
 * </Cell>
 *
 * // Link
 * <Cell kind="regular" size="default">
 *   <TextContent text="View details" href="/details" />
 * </Cell>
 * ```
 */
export const TextContent: FC<TextContentProps> = ({
  text,
  disableTruncation = false,
  tooltip,
  className,
  variant = 'default',
  href,
}) => {
  const getTextProps = () => {
    switch (variant) {
      case 'strong':
        return { size: 'medium' as const, bold: true };
      case 'small':
        return { size: 'small' as const, bold: false };
      case 'default':
      default:
        return { size: 'medium' as const, bold: false };
    }
  };

  const { size, bold } = getTextProps();
  const isInteractive = Boolean(href);
  const isEmpty = text == null || text.trim() === '';
  const displayText = isEmpty ? 'Empty' : text;

  const shouldTruncate = !disableTruncation;

  return (
    <Text
      size={size}
      bold={bold}
      elementType={href ? 'a' : 'span'}
      className={className}
      maxLines={shouldTruncate ? 1 : undefined}
      style={{
        cursor: isInteractive ? 'pointer' : undefined,
        textDecoration: href ? 'underline' : undefined,
        textDecorationStyle: href ? 'solid' : undefined,
        textDecorationSkipInk: href ? ('none' as const) : undefined,
        whiteSpace: disableTruncation ? 'normal' : 'nowrap',
        ...(disableTruncation && { overflowWrap: 'anywhere' as const }),
        // Empty state color
        color: isEmpty ? vars.theme.interactive.fill.secondary.content.onDisabled : undefined,
      }}
      title={tooltip || (shouldTruncate ? displayText : undefined)}
      // @ts-expect-error - href is specific to anchor elements, not in base HTMLAttributes
      href={href}
    >
      {displayText}
    </Text>
  );
};
